import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Box, Paper, Grid, Typography, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow, Chip, } from '@mui/material';
import { fK, fX, resultColor } from '../utils/formatters';
const KEYWORDS = [
    'Static', 'Video', 'UGC', 'Retarget', 'Prospecting', 'Story',
    'Carousel', 'DPA', 'BAU', 'Sale', 'Collection', 'Brand',
    'Dynamic', 'Reels', 'Image',
];
const FailInsightsTab = ({ report }) => {
    const ads = report.ads || [];
    const fa = useMemo(() => ads.filter((a) => a.result === 'Fail'), [ads]);
    if (fa.length === 0) {
        return (_jsx(Box, { sx: { p: 6, textAlign: 'center', color: '#667085', fontSize: 14 }, children: "No Fail ads in this period." }));
    }
    // Failure modes (standalone logic)
    const modes = useMemo(() => {
        const d = {
            all3: 0, ocpcOctr: 0, ocpcRoas: 0, octrRoas: 0,
            ocpcOnly: 0, octrOnly: 0, roasOnly: 0,
        };
        fa.forEach((a) => {
            const fo = a.ocpcRating === 'Fail';
            const ft = a.octrRating === 'Fail';
            const fr = a.roasRating === 'Fail';
            if (fo && ft && fr)
                d.all3++;
            else if (fo && ft)
                d.ocpcOctr++;
            else if (fo && fr)
                d.ocpcRoas++;
            else if (ft && fr)
                d.octrRoas++;
            else if (fo)
                d.ocpcOnly++;
            else if (ft)
                d.octrOnly++;
            else if (fr)
                d.roasOnly++;
        });
        return d;
    }, [fa]);
    const oF = fa.filter((a) => a.ocpcRating === 'Fail').length;
    const tF = fa.filter((a) => a.octrRating === 'Fail').length;
    const rF = fa.filter((a) => a.roasRating === 'Fail').length;
    // OCPC Failure Severity (variance %) — NEW
    const oVars = useMemo(() => fa
        .filter((a) => a.atOCPC != null && a.bench?.ocpc > 0)
        .map((a) => ((a.atOCPC - a.bench.ocpc) / a.bench.ocpc) * 100), [fa]);
    const severityBuckets = useMemo(() => [
        {
            label: 'Slight',
            desc: '21–50% above benchmark',
            count: oVars.filter((v) => v > 20 && v <= 50).length,
            color: '#EE2A7B',
            bg: '#FCE8F2',
        },
        {
            label: 'Bad',
            desc: '51–100% above benchmark',
            count: oVars.filter((v) => v > 50 && v <= 100).length,
            color: '#C0145A',
            bg: '#FCE8F2',
        },
        {
            label: 'Terrible',
            desc: '>100% above benchmark',
            count: oVars.filter((v) => v > 100).length,
            color: '#8B0A3E',
            bg: '#F8D0E4',
        },
    ], [oVars]);
    const top10 = useMemo(() => [...fa]
        .sort((a, b) => (b.wastedSpend || 0) - (a.wastedSpend || 0))
        .slice(0, 10), [fa]);
    const totalWasted = fa.reduce((s, a) => s + (a.wastedSpend || 0), 0);
    const p = report.projections || {};
    const wastedPct = p.totalSpend > 0 ? Math.round((totalWasted / p.totalSpend) * 100) : 0;
    // NEW (Batch 8) — keywords now also track wasted $ per keyword
    // (standalone line 1339-1342). Replaces the old kwCounts grid below.
    const kwC = useMemo(() => KEYWORDS.map((kw) => {
        const matches = fa.filter((a) => (a.ad_name || '').toLowerCase().includes(kw.toLowerCase()));
        return {
            kw,
            count: matches.length,
            wasted: matches.reduce((s, a) => s + (a.wastedSpend || 0), 0),
        };
    })
        .filter((k) => k.count > 0)
        .sort((a, b) => b.count - a.count), [fa]);
    const kwCounts = KEYWORDS.map((kw) => ({
        kw,
        count: fa.filter((a) => (a.ad_name || '').toLowerCase().includes(kw.toLowerCase())).length,
    })).filter((k) => k.count > 0);
    return (_jsxs(Box, { sx: { p: 3 }, children: [_jsxs(Grid, { container: true, spacing: 1.5, sx: { mb: 3 }, children: [_jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsx(SummaryCard, { label: "Fail Ads", value: String(fa.length), sub: `${Math.round((fa.length / ads.length) * 100)}% of evaluated ads`, color: resultColor('Fail') }) }), _jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsx(SummaryCard, { label: "Wasted Spend (Failed Ads)", value: fK(totalWasted), sub: `${wastedPct}% of ${fK(p.totalSpend)} total spend`, color: resultColor('Fail') }) }), _jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsx(SummaryCard, { label: "OCPC Failures", value: String(oF), sub: `${Math.round((oF / fa.length) * 100)}% of fail ads`, color: resultColor('Fail') }) }), _jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsx(SummaryCard, { label: "ROAS Failures", value: String(rF), sub: `${Math.round((rF / fa.length) * 100)}% of fail ads`, color: resultColor('Fail') }) })] }), _jsxs(Grid, { container: true, spacing: 2.5, sx: { mb: 3 }, children: [_jsx(Grid, { size: { xs: 12, md: 6 }, children: _jsxs(Paper, { elevation: 0, sx: {
                                border: '1px solid #EAECF0',
                                borderRadius: '8px',
                                bgcolor: '#fff',
                                overflow: 'hidden',
                            }, children: [_jsxs(Box, { sx: { bgcolor: '#FAFAFA', px: 2, py: 1.5, borderBottom: '1px solid #EAECF0' }, children: [_jsx(Typography, { sx: { fontWeight: 700, fontSize: 13, color: '#101828' }, children: "What Is Causing Failures?" }), _jsx(Typography, { sx: { fontSize: 11, color: '#667085', mt: 0.25 }, children: "Breakdown of which metric combinations are failing" })] }), _jsxs(Box, { sx: { p: 2 }, children: [_jsx(FailBar, { label: "All 3 metrics failed", count: modes.all3, total: fa.length }), _jsx(FailBar, { label: "OCPC + OCTR failed", count: modes.ocpcOctr, total: fa.length }), _jsx(FailBar, { label: "OCPC + ROAS failed", count: modes.ocpcRoas, total: fa.length }), _jsx(FailBar, { label: "OCTR + ROAS failed", count: modes.octrRoas, total: fa.length }), _jsx(FailBar, { label: "OCPC only", count: modes.ocpcOnly, total: fa.length }), _jsx(FailBar, { label: "OCTR only", count: modes.octrOnly, total: fa.length }), _jsx(FailBar, { label: "ROAS only", count: modes.roasOnly, total: fa.length })] })] }) }), _jsx(Grid, { size: { xs: 12, md: 6 }, children: _jsxs(Paper, { elevation: 0, sx: {
                                border: '1px solid #EAECF0',
                                borderRadius: '8px',
                                bgcolor: '#fff',
                                overflow: 'hidden',
                            }, children: [_jsxs(Box, { sx: { bgcolor: '#FAFAFA', px: 2, py: 1.5, borderBottom: '1px solid #EAECF0' }, children: [_jsx(Typography, { sx: { fontWeight: 700, fontSize: 13, color: '#101828' }, children: "OCPC Failure Severity" }), _jsx(Typography, { sx: { fontSize: 11, color: '#667085', mt: 0.25 }, children: "How far above benchmark are failing ads?" })] }), _jsxs(Box, { sx: { p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }, children: [severityBuckets.map((b) => (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.75 }, children: [_jsxs(Box, { sx: { width: 75, flexShrink: 0 }, children: [_jsx(Typography, { sx: { fontSize: 12, fontWeight: 700, color: b.color }, children: b.label }), _jsx(Typography, { sx: { fontSize: 10, color: '#667085', mt: 0.25 }, children: b.desc })] }), _jsx(Box, { sx: {
                                                        flex: 1,
                                                        bgcolor: '#F2F4F7',
                                                        borderRadius: '6px',
                                                        height: 28,
                                                        overflow: 'hidden',
                                                    }, children: _jsx(Box, { sx: {
                                                            bgcolor: b.bg,
                                                            width: `${oF > 0
                                                                ? Math.min((b.count / oF) * 100, 100)
                                                                : 0}%`,
                                                            height: '100%',
                                                            borderRadius: '6px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            pl: 1.25,
                                                        }, children: b.count > 0 && (_jsxs(Typography, { sx: {
                                                                fontSize: 11,
                                                                fontWeight: 700,
                                                                color: b.color,
                                                            }, children: [b.count, " ads"] })) }) }), _jsxs(Typography, { sx: {
                                                        width: 40,
                                                        textAlign: 'right',
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                        color: b.color,
                                                        flexShrink: 0,
                                                    }, children: [oF > 0 ? Math.round((b.count / oF) * 100) : 0, "%"] })] }, b.label))), oF === 0 && (_jsx(Typography, { sx: { color: '#667085', fontSize: 12 }, children: "No OCPC failures found." }))] })] }) })] }), _jsxs(Paper, { elevation: 0, sx: {
                    border: '1px solid #EAECF0',
                    borderRadius: '8px',
                    mb: 3,
                    bgcolor: '#fff',
                    overflow: 'hidden',
                }, children: [_jsxs(Box, { sx: {
                            px: 2,
                            py: 1.5,
                            borderBottom: '1px solid #EAECF0',
                            bgcolor: '#FAFAFA',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }, children: [_jsxs(Box, { children: [_jsx(Typography, { sx: { fontWeight: 700, fontSize: 13, color: '#101828' }, children: "Top 10 Wasted Spend" }), _jsx(Typography, { sx: { fontSize: 11, color: '#667085', mt: 0.25 }, children: "Fail ads ranked by post-threshold spend" })] }), _jsxs(Typography, { sx: { fontSize: 12, color: '#667085', fontWeight: 600 }, children: [fK(top10.reduce((s, a) => s + (a.wastedSpend || 0), 0)), ' ', "combined"] })] }), _jsx(Box, { children: top10.map((ad, i) => {
                            const totalWastedPct = p.totalSpend > 0
                                ? Math.round(((ad.wastedSpend || 0) / p.totalSpend) * 100)
                                : 0;
                            return (_jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 2,
                                    py: 1.5,
                                    borderBottom: i < top10.length - 1 ? '1px solid #F2F4F7' : 'none',
                                    gap: 2,
                                }, children: [_jsx(Typography, { sx: {
                                            width: 24,
                                            fontSize: 12,
                                            color: '#98A2B3',
                                            fontWeight: 600,
                                        }, children: i + 1 }), _jsx(Typography, { sx: {
                                            flex: 1,
                                            fontSize: 12.5,
                                            color: '#101828',
                                            fontWeight: 500,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }, title: ad.ad_name, children: ad.ad_name }), _jsx(Typography, { sx: {
                                            fontSize: 12,
                                            color: resultColor('Fail'),
                                            fontWeight: 700,
                                            minWidth: 110,
                                            textAlign: 'right',
                                        }, children: fK(ad.wastedSpend) }), _jsx(Box, { sx: { width: 100 }, children: _jsx(LinearProgress, { variant: "determinate", value: Math.min(totalWastedPct * 4, 100), sx: {
                                                height: 6,
                                                borderRadius: 3,
                                                bgcolor: '#F2F4F7',
                                                '& .MuiLinearProgress-bar': {
                                                    bgcolor: resultColor('Fail'),
                                                },
                                            } }) }), _jsxs(Typography, { sx: {
                                            fontSize: 11,
                                            color: '#667085',
                                            minWidth: 45,
                                            textAlign: 'right',
                                        }, children: [totalWastedPct, "%"] }), _jsx(Typography, { sx: {
                                            fontSize: 12,
                                            color: '#475467',
                                            minWidth: 70,
                                            textAlign: 'right',
                                        }, children: fX(ad.aftROAS ?? ad.atROAS) })] }, ad.ad_id));
                        }) })] }), _jsx(Grid, { container: true, spacing: 2.5, children: _jsx(Grid, { size: { xs: 12 }, children: _jsxs(Paper, { elevation: 0, sx: {
                            border: '1px solid #EAECF0',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            bgcolor: '#fff',
                            height: '100%',
                        }, children: [_jsxs(Box, { sx: {
                                    bgcolor: '#FAFAFA',
                                    px: 2,
                                    py: 1.5,
                                    borderBottom: '1px solid #EAECF0',
                                }, children: [_jsx(Typography, { sx: { fontWeight: 700, fontSize: 13, color: '#101828' }, children: "Creative / Type Patterns" }), _jsx(Typography, { sx: { fontSize: 11, color: '#667085', mt: 0.25 }, children: "Keyword frequency in failing ad names" }), _jsxs(Box, { sx: {
                                            mt: 1.25,
                                            bgcolor: '#F9FAFB',
                                            border: '1px solid #EAECF0',
                                            borderRadius: '8px',
                                            px: 1.5,
                                            py: 1,
                                        }, children: [_jsx(Typography, { sx: { fontSize: 10, fontWeight: 700, color: '#475467', mb: 0.5 }, children: "\u2139\uFE0F How to read this table" }), _jsxs(Typography, { sx: { fontSize: 10, color: '#667085', lineHeight: 1.6 }, children: [_jsx("strong", { style: { color: '#475467' }, children: "Ads column:" }), " counts how many of the ", fa.length, " Fail ads contain that keyword in their ad name. One ad can match multiple keywords \u2014 e.g. an ad named \"Static Video UGC Sale LP\" is counted in all 4 rows.", ' ', _jsxs("strong", { style: { color: '#475467' }, children: ["The column totals will exceed ", fa.length, "."] }), ' ', "The % shows share of all Fail ads containing each keyword."] })] })] }), kwC.length === 0 ? (_jsx(Box, { sx: { p: 2.5, color: '#667085', fontSize: 12 }, children: "No keyword matches found." })) : (_jsxs(Table, { size: "small", children: [_jsx(TableHead, { children: _jsxs(TableRow, { sx: { bgcolor: '#FAFAFA' }, children: [_jsx(TableCell, { sx: {
                                                        fontWeight: 700, fontSize: 12, color: '#667085',
                                                        py: 1, px: 1.5, borderBottom: '1px solid #EAECF0',
                                                    }, children: "Keyword" }), _jsx(TableCell, { sx: {
                                                        fontWeight: 700, fontSize: 12, color: '#667085',
                                                        py: 1, px: 1.5, textAlign: 'right',
                                                        borderBottom: '1px solid #EAECF0',
                                                    }, children: "Ads" }), _jsxs(TableCell, { sx: {
                                                        fontWeight: 700, fontSize: 12, color: '#667085',
                                                        py: 1, px: 1.5, textAlign: 'right',
                                                        borderBottom: '1px solid #EAECF0',
                                                    }, children: ["% of ", fa.length, " Fail"] }), _jsx(TableCell, { sx: {
                                                        fontWeight: 700, fontSize: 12, color: '#667085',
                                                        py: 1, px: 1.5, textAlign: 'right',
                                                        borderBottom: '1px solid #EAECF0',
                                                    }, children: "Wasted $" })] }) }), _jsx(TableBody, { children: kwC.map(({ kw, count, wasted }, i) => (_jsxs(TableRow, { sx: {
                                                bgcolor: i % 2 === 0 ? '#fff' : '#FAFBFC',
                                                '& td': { borderBottom: '1px solid #F2F4F7' },
                                            }, children: [_jsx(TableCell, { sx: { py: 1, px: 1.5, fontSize: 12 }, children: _jsx(Chip, { label: kw, size: "small", sx: {
                                                            bgcolor: '#F2F4F7',
                                                            color: '#475467',
                                                            fontSize: 11,
                                                            fontWeight: 600,
                                                            height: 22,
                                                            borderRadius: '5px',
                                                        } }) }), _jsx(TableCell, { sx: {
                                                        py: 1, px: 1.5, fontSize: 14,
                                                        textAlign: 'right', fontWeight: 600, color: '#101828',
                                                    }, children: count }), _jsxs(TableCell, { sx: {
                                                        py: 1, px: 1.5, fontSize: 14,
                                                        textAlign: 'right', color: '#667085',
                                                    }, children: [Math.round((count / fa.length) * 100), "%"] }), _jsx(TableCell, { sx: {
                                                        py: 1, px: 1.5, fontSize: 14,
                                                        textAlign: 'right', fontWeight: 700,
                                                        color: resultColor('Fail'),
                                                    }, children: fK(wasted) })] }, kw))) })] }))] }) }) })] }));
};
const SummaryCard = ({ label, value, sub, color }) => (_jsxs(Paper, { elevation: 0, sx: {
        p: 2.25,
        border: '1px solid #EAECF0',
        borderLeft: `4px solid ${color}`,
        borderRadius: '8px',
        bgcolor: '#fff',
        height: '100%',
    }, children: [_jsx(Typography, { sx: {
                fontSize: 9,
                color: '#98A2B3',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                mb: 1,
            }, children: label }), _jsx(Typography, { sx: {
                fontSize: 28,
                fontWeight: 800,
                color: '#101828',
                letterSpacing: '-0.02em',
                lineHeight: 1,
            }, children: value }), sub && (_jsx(Typography, { sx: { fontSize: 11, color: '#98A2B3', mt: 1 }, children: sub }))] }));
const FailBar = ({ label, count, total, }) => {
    const pct = total > 0 ? (count / total) * 100 : 0;
    return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2, py: 0.75 }, children: [_jsx(Typography, { sx: { fontSize: 12, color: '#344054', minWidth: 200 }, children: label }), _jsx(Box, { sx: { flex: 1 }, children: _jsx(LinearProgress, { variant: "determinate", value: pct, sx: {
                        height: 6,
                        borderRadius: 3,
                        bgcolor: '#F2F4F7',
                        '& .MuiLinearProgress-bar': { bgcolor: resultColor('Fail') },
                    } }) }), _jsxs(Typography, { sx: {
                    fontSize: 12,
                    fontWeight: 700,
                    color: resultColor('Fail'),
                    minWidth: 70,
                    textAlign: 'right',
                }, children: [count, " (", Math.round(pct), "%)"] })] }));
};
export default FailInsightsTab;
